import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { EmailTemplate, EmailSender } from '../../services/emailApi';
import { TEMPLATE_TYPE_MAP } from '../../services/emailApi';

// Lazy-load a rich text editor if added later; fallback to textarea now to avoid adding heavy deps.
// You can replace with React-Quill or TinyMCE easily.

interface TemplateEditorProps {
  template: EmailTemplate;
  variables: string[]; // e.g. ['ClientName','BookingDate']
  onChange: (updated: EmailTemplate) => void;
  onPreview: (t: EmailTemplate) => void;
  onSendTest: (t: EmailTemplate, senderId: string, toEmail: string) => void;
  senders?: EmailSender[];
  onClose?: () => void;
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({
  template,
  variables,
  onChange,
  onPreview,
  onSendTest,
  onClose,
  senders = [],
}) => {
  const [localTemplate, setLocalTemplate] = useState<EmailTemplate>(template);
  const [testEmail, setTestEmail] = useState('');
  const [senderId, setSenderId] = useState('');

  useEffect(() => {
    setLocalTemplate(template);
  }, [template]);

  const updateField = (key: keyof EmailTemplate, value: any) => {
    const updated = { ...localTemplate, [key]: value };
    setLocalTemplate(updated);
    onChange(updated);
  };

  const insertVariable = (variable: string) => {
    const insertion = `{{${variable}}}`;
    updateField('htmlContent', (localTemplate.htmlContent || '') + insertion);
  };

  return (
    <div className="p-4 bg-white rounded shadow w-full max-w-3xl">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-primary">Edit Template</h2>
        {onClose && (
          <button onClick={onClose} className="text-sm text-red-600 hover:underline">Close</button>
        )}
      </div>
      <div className="grid gap-3 mb-4">
        <div>
          <label className="text-sm font-medium text-secondary">Name</label>
          <input
            className="mt-1 w-full border rounded px-3 py-2 text-sm"
            value={localTemplate.name}
            onChange={(e) => updateField('name', e.target.value)}
          />
        </div>
        <div>
          <label className="text-sm font-medium text-secondary">Subject</label>
          <input
            className="mt-1 w-full border rounded px-3 py-2 text-sm"
            value={localTemplate.subject || ''}
            onChange={(e) => updateField('subject' as any, e.target.value)}
          />
        </div>
        <div>
          <label className="text-sm font-medium text-secondary">Type</label>
          <select
            className="mt-1 w-full border rounded px-3 py-2 text-sm"
            value={localTemplate.type || 'Booking'}
            onChange={(e) => updateField('type', e.target.value)}
          >
            {Object.keys(TEMPLATE_TYPE_MAP).filter(k => isNaN(Number(k))).map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-secondary">Status</label>
          <select
            className="mt-1 w-full border rounded px-3 py-2 text-sm"
            value={localTemplate.status || 'Active'}
            onChange={(e) => updateField('status', e.target.value)}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="flex gap-4 mb-4">
        <div className="w-2/3">
          <label className="text-sm font-medium text-secondary">HTML Content</label>
          <ReactQuill
            className="mt-1 bg-white"
            theme="snow"
            value={localTemplate.htmlContent || ''}
            onChange={val => updateField('htmlContent', val)}
            placeholder="Write HTML email body here. Use variables like {{ClientName}}"
            style={{ height: 260, marginBottom: 8 }}
          />
        </div>
        <div className="w-1/3">
          <label className="text-sm font-medium text-secondary flex justify-between items-center">
            Variables
            <span className="text-xs text-gray-500">Click to insert</span>
          </label>
          <div className="mt-1 space-y-1 max-h-64 overflow-auto border rounded p-2 bg-background">
            {variables.length === 0 && (
              <div className="text-xs text-gray-500">No variables</div>
            )}
            {variables.map(v => (
              <button
                key={v}
                type="button"
                onClick={() => insertVariable(v)}
                className="block w-full text-left text-xs px-2 py-1 rounded bg-white hover:bg-primary hover:text-white border"
              >
                {`{{${v}}}`}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={() => onPreview(localTemplate)}
          className="px-4 py-2 bg-primary text-white rounded text-sm hover:opacity-90"
        >
          Preview
        </button>
        {senders?.length > 0 && (
          <select
            value={senderId}
            onChange={(e) => setSenderId(e.target.value)}
            className="border rounded px-3 py-2 text-sm"
          >
            <option value="">Select sender</option>
            {senders.map(s => (
              <option key={s.id || s.emailAddress} value={String(s.id)}>{s.emailAddress}</option>
            ))}
          </select>
        )}
        <input
          type="email"
          placeholder="Test email address"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          className="border rounded px-3 py-2 text-sm"
        />
        <button
          disabled={!testEmail || !senderId}
          onClick={() => onSendTest(localTemplate, senderId, testEmail)}
          className="px-4 py-2 bg-secondary text-white rounded text-sm hover:opacity-90 disabled:opacity-40"
        >
          Send Test
        </button>
      </div>
    </div>
  );
};

export default TemplateEditor;
